/**
 * Judged play: how far the hit-effect ticks of each hold reach (W1-06; R§5.1, R§5.5, A§7.5).
 *
 * Phira emits a hold tick only on frames where the hold is still held, and a burst, once emitted, lives its full
 * hitFxDuration. The JudgeView says whether a hold is held NOW (`holding`) and, optionally, when it was released
 * (`releasedAt`, CR-W1-06-6), and a hold released early may turn into a miss. So, per engine and
 * JudgeView, this tracker records for every hold with a status, from the frames it is shown:
 *   - `heldUntil`: the latest frame time at which the hold was reported as held;
 *   - `grade`: the grade of its head judgement (the first Perfect/Good seen), which keeps colouring the ticks that
 *     were emitted before a later miss.
 * The ticks of a hold whose head was hit (Perfect/Good) are drawn up to:
 *   - `releasedAt` (exclusive) when the status carries it;
 *   - t while the hold is held;
 *   - `heldUntil` (inclusive) once it is not held any more (released early, or completed): the bursts emitted until
 *     the release keep fading out, no new ones start;
 *   - its end time when it was never seen held but t is past its end with a Perfect/Good grade (the frames started
 *     after the hold, for example after a seek): a completed hold;
 *   - nowhere when it was released early and the release was not seen (its release time is unknown).
 * A status that disappears (restart) or whose judgement time changes (a new judgement) resets the record; a new
 * JudgeView object starts a new tracker.
 */

import type { NoteId } from '../../model/types';
import type { EngineCompileContext, JudgeView } from '../types';
import { cacheOf, globalOf } from './store';

type Status = ReturnType<JudgeView['status']>;

interface HoldRecord {
  /** Judgement time of the head the record belongs to. */
  at: number;
  /** Grade of the head judgement (null until a Perfect/Good was seen). */
  grade: 'perfect' | 'good' | null;
  /** Latest frame time at which the hold was reported as held (NaN = never seen held). */
  heldUntil: number;
}

/** Tick range of a hold at a frame (scratch object filled by HoldTickTracker.window). */
export interface HoldTickWindow {
  /** Head judgement time (ticks start one frame later). */
  at: number;
  grade: 'perfect' | 'good';
  /** Last tick time drawn. */
  until: number;
  /** Whether a tick exactly at `until` is drawn. */
  inclusive: boolean;
}

export class HoldTickTracker {
  readonly judge: JudgeView;
  private readonly records = new Map<number, HoldRecord>();

  constructor(judge: JudgeView) {
    this.judge = judge;
  }

  /** Records currently kept (tests). */
  get size(): number {
    return this.records.size;
  }

  /**
   * Observes hold `note` (status `st`, end time `end`) at frame time t and fills `out` with the range of its ticks;
   * false when it draws no ticks at t.
   */
  window(note: NoteId, st: Status, t: number, end: number, out: HoldTickWindow): boolean {
    const id = note as number;
    if (st === null) {
      this.records.delete(id);
      return false;
    }
    let rec = this.records.get(id);
    if (rec === undefined || !Object.is(rec.at, st.at)) {
      rec = { at: st.at, grade: null, heldUntil: Number.NaN };
      this.records.set(id, rec);
    }
    if (rec.grade === null && (st.grade === 'perfect' || st.grade === 'good')) rec.grade = st.grade;
    if (!(st.at <= t)) return false;
    if (st.holding && !(rec.heldUntil >= t)) rec.heldUntil = t;
    const grade = rec.grade;
    if (grade === null) return false;
    out.at = st.at;
    out.grade = grade;
    const releasedAt = st.releasedAt;
    if (releasedAt !== undefined && !Number.isNaN(releasedAt)) {
      out.until = releasedAt;
      out.inclusive = false;
      return true;
    }
    if (st.holding) {
      out.until = t;
      out.inclusive = true;
      return true;
    }
    if (!Number.isNaN(rec.heldUntil)) {
      out.until = rec.heldUntil;
      out.inclusive = true;
      return true;
    }
    if (st.grade !== 'miss' && t >= end) {
      out.until = end;
      out.inclusive = false;
      return true;
    }
    return false;
  }
}

interface TrackerSlot {
  tracker: HoldTickTracker | null;
}

/** The hold-tick tracker of this engine for `judge` (a new JudgeView starts a new one). */
export function holdTickTracker(ctx: EngineCompileContext, judge: JudgeView): HoldTickTracker {
  const slot = cacheOf<TrackerSlot>(globalOf(ctx), 'holdTicks', () => ({ tracker: null }));
  let tr = slot.tracker;
  if (tr?.judge !== judge) {
    tr = new HoldTickTracker(judge);
    slot.tracker = tr;
  }
  return tr;
}

/** A blank window (scratch). */
export function makeHoldTickWindow(): HoldTickWindow {
  return { at: 0, grade: 'perfect', until: 0, inclusive: true };
}
